import { useState } from "react";
import { Button, CircularProgress, Stack } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Send from "@mui/icons-material/Send";
import Warning from "@mui/icons-material/Warning";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useUpdateSubmissionStatus } from "hooks/useSubmissionMutations";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ForceCompleteDialog } from "./ForceCompleteDialog";

interface UpdateSubmissionStatusButtonProps {
  submissionId: string;
  tenantId: string;
  currentStatus?: SubmissionStatus;
  isStaff: boolean;
  isEditMode: boolean;
  isSendToReviewersDisabled: boolean;
  loading: boolean;
  formLoading: boolean;
  onMoveToReview: () => void;
  onSuccess?: () => void;
  onForceCompleteSuccess?: () => void;
}

export function UpdateSubmissionStatusButton({
  submissionId,
  tenantId,
  currentStatus,
  isStaff,
  isEditMode,
  isSendToReviewersDisabled,
  loading,
  formLoading,
  onMoveToReview,
  onSuccess,
  onForceCompleteSuccess,
}: UpdateSubmissionStatusButtonProps) {
  const alerts = useAlerts();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [forceCompleteDialogOpen, setForceCompleteDialogOpen] = useState(false);
  const [updateSubmissionStatus, { loading: updating }] =
    useUpdateSubmissionStatus();

  const handleClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    try {
      await updateSubmissionStatus({
        variables: {
          input: {
            submissionId,
            submissionStatus: SubmissionStatus.FilingComplete,
          },
        },
      });

      alerts.success("Submission status updated to Filing Complete");
      setConfirmDialogOpen(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update submission status:", error);
      alerts.error("Failed to update submission status");
    }
  };

  // Show "🎉 Filing Complete" text if status is FILING_COMPLETE
  if (currentStatus === SubmissionStatus.FilingComplete) {
    return (
      <Button
        variant="text"
        disabled
        sx={{
          color: "text.primary",
          fontSize: "1rem",
          fontWeight: 500,
          cursor: "default",
          "&.Mui-disabled": {
            color: "text.primary",
          },
        }}
      >
        🎉 Filing Complete
      </Button>
    );
  }

  // Helper to determine if Force Complete should be shown
  // Note: We've already returned early for FILING_COMPLETE status above
  const canShowForceComplete =
    isEditMode && isStaff && currentStatus !== SubmissionStatus.NotReporting;

  // Show "Mark as Filing Complete" button if status is PROCESSING_SUBMISSION and user is staff
  if (
    isEditMode &&
    currentStatus === SubmissionStatus.ProcessingSubmission &&
    isStaff
  ) {
    return (
      <>
        <Stack direction="row" spacing={1}>
          {canShowForceComplete && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Warning />}
              onClick={() => setForceCompleteDialogOpen(true)}
              sx={{ textTransform: "uppercase" }}
            >
              Force Complete
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            startIcon={
              updating ? <CircularProgress size={20} /> : <CheckCircle />
            }
            onClick={handleClick}
            disabled={updating}
            sx={{ textTransform: "uppercase" }}
          >
            Mark as filing complete
          </Button>
        </Stack>

        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleConfirm}
          header="Confirm Status Update"
          info="Are you sure you want to mark this submission as Filing Complete? This action cannot be undone."
          confirmText="Mark as Complete"
          cancelText="Cancel"
        />

        <ForceCompleteDialog
          open={forceCompleteDialogOpen}
          onClose={() => setForceCompleteDialogOpen(false)}
          submissionId={submissionId}
          tenantId={tenantId}
          onSuccess={onForceCompleteSuccess}
        />
      </>
    );
  }

  // Show "Move to Review" button for new submissions or statuses that aren't PROCESSING_SUBMISSION
  if (
    !currentStatus ||
    currentStatus === SubmissionStatus.NotStarted ||
    currentStatus === SubmissionStatus.InProgress ||
    isStaff
  ) {
    const buttonLabel = isStaff ? "Save & Move to Review" : "Move to Review";

    return (
      <>
        <Stack direction="row" spacing={1}>
          {canShowForceComplete && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Warning />}
              onClick={() => setForceCompleteDialogOpen(true)}
              sx={{ textTransform: "uppercase" }}
            >
              Force Complete
            </Button>
          )}
          <Button
            variant="contained"
            disabled={isSendToReviewersDisabled || loading || formLoading}
            endIcon={<Send />}
            onClick={onMoveToReview}
            sx={{ textTransform: "uppercase" }}
          >
            {buttonLabel}
          </Button>
        </Stack>

        <ForceCompleteDialog
          open={forceCompleteDialogOpen}
          onClose={() => setForceCompleteDialogOpen(false)}
          submissionId={submissionId}
          tenantId={tenantId}
          onSuccess={onForceCompleteSuccess}
        />
      </>
    );
  }

  // Don't show any button in other cases
  return null;
}
